import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { Entry, EntryJSON } from "../models/entries";
import { PlaySong } from "../Toolbar/PlaySong";
import { PlayList } from "../Toolbar/PlayList";
import { TrophyJSON } from "../models/trophies";
import { ExpandedEntryJSON, IsExpandedEntryJSON } from "../../../src/apidefs";
import { EntryType } from "../Compo/CompoPage";
import { CompoType } from "../models/compos";
import EntryStats from "../Entry/EntryStats";
import { EntryIcon } from "./EntryIcon";
import { RLink } from "./RLink";
import { ScoreDisplay } from "./ScoreDisplay";
import { useUser } from "../state";

const typeFilter: { type: EntryType | "all" }[] = [
  { type: "all" },
  { type: "song" },
  { type: "story" },
  { type: "art" },
];

const compoFilter: { type: CompoType | "all"; name: string }[] = [
  { type: "all", name: "All" },
  { type: "main", name: "Main" },
  { type: "day", name: "Day" },
  { type: "hour", name: "Hour" },
  { type: "meme", name: "Meme" },
];

export function EntryList({
  entries,
  defaultEntryTypeFilter,
  defaultCompoFilter,
  hideFilter,
  hidePlayAll,
  hidePlay,
  favoriteEntryIDs,
  commentedEntryIDs,
  trophies,
  avatar_urls,
  associatedCompos,
  hideStats,
  scores,
  usernames,
}: {
  entries: (EntryJSON | ExpandedEntryJSON)[];
  defaultEntryTypeFilter?: EntryType | "all";
  defaultCompoFilter?: CompoType | "all";
  hideFilter?: boolean;
  hidePlayAll?: boolean;
  hidePlay?: boolean;
  favoriteEntryIDs?: number[];
  commentedEntryIDs?: number[];
  trophies?: TrophyJSON[];
  avatar_urls?: string[][];
  associatedCompos?: { compo_id: number; name: string; type: string }[];
  hideStats?: boolean;
  scores?: number[];
  usernames?: string[][];
}) {
  const userState = useUser();
  const user = userState.type === "logged-in" ? userState.user : undefined;
  const history = useHistory();
  const [entryTypeFilter, setEntryTypeFilter] = useState<EntryType | "all">(
    defaultEntryTypeFilter || "all"
  );
  const [compoTypeFilter, setCompoTypeFilter] = useState<CompoType | "all">(
    defaultCompoFilter || "all"
  );
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Oh god no.
  useEffect(() => {
    setCompoTypeFilter(defaultCompoFilter || "all");
    setEntryTypeFilter(defaultEntryTypeFilter || "all");
    setFiltersVisible(false);
  }, [defaultCompoFilter, defaultEntryTypeFilter, hideFilter]);

  const filterList = useCallback(
    (filter: {
      entryType: EntryType | "all";
      compoType: CompoType | "all";
    }) => {
      const { entryType, compoType } = filter;

      const passingIndices: number[] = [];
      entries.forEach((entry, i) => {
        if (entryType !== "all" && entry.type !== entryType) {
          return;
        }

        if (compoType !== "all" && associatedCompos) {
          const compo = associatedCompos[i];
          if (!compo || compo.type !== compoType) {
            return;
          }
        }

        passingIndices.push(i);
      });

      const filteredEntries = passingIndices.map((i) => entries[i]);

      const filteredCompos = associatedCompos
        ? passingIndices.map((i) => associatedCompos[i])
        : undefined;

      const filteredProfilePics = avatar_urls
        ? passingIndices.map((i) => avatar_urls[i])
        : undefined;

      const filteredScores = scores
        ? passingIndices.map((i) => scores[i])
        : undefined;

      const filteredUsernames = usernames
        ? passingIndices.map((i) => usernames[i])
        : undefined;

      return {
        filteredEntries,
        filteredCompos,
        filteredProfilePics,
        filteredScores,
        filteredUsernames,
      };
    },
    [entries, associatedCompos, avatar_urls, scores, usernames]
  );

  const { filteredEntries, filteredCompos, filteredProfilePics, filteredScores, filteredUsernames } = useMemo(
    () =>
      filterList({
        entryType: entryTypeFilter,
        compoType: compoTypeFilter,
      }),
    [filterList, entryTypeFilter, compoTypeFilter]
  );

  const entryTypesAndEntryCounts = useMemo(() => {
    const counts: { type: EntryType | "all"; count: number }[] = [];

    for (const { type } of typeFilter) {
      counts.push({
        type,
        count: filterList({
          entryType: type,
          compoType: compoTypeFilter,
        }).filteredEntries.length,
      });
    }

    return counts.filter(({ count }) => count > 0);
  }, [filterList, compoTypeFilter]);

  const compoTypesAndEntryCounts = useMemo(() => {
    const counts: { name: string; type: CompoType | "all"; count: number }[] =
      [];

    for (const { type, name } of compoFilter) {
      counts.push({
        type,
        name,
        count: filterList({
          entryType: entryTypeFilter,
          compoType: type,
        }).filteredEntries.length,
      });
    }

    return counts.filter(({ count }) => count > 0);
  }, [filterList, entryTypeFilter]);

  if (entries.length === 0) {
    return <span> </span>;
  }

  const showTopSection = !hidePlayAll || !hideFilter;

  return (
    <div>
      {showTopSection && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {!hidePlayAll && (
            <PlayList entries={filteredEntries} desc="Play All" />
          )}
          {!hideFilter && (
            <button
              type="button"
              onClick={() => setFiltersVisible(!filtersVisible)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                filtersVisible
                  ? "bg-chips-gold/20 text-chips-gold"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
              }`}
            >
              <i
                className={`fa ${filtersVisible ? "fa-caret-down" : "fa-caret-right"} text-xs`}
                aria-hidden="true"
              />
              Filter...
            </button>
          )}
        </div>
      )}

      {filtersVisible && (
        <div className="mb-4 space-y-3 rounded-lg bg-slate-50 p-4 dark:bg-slate-700/50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Show
            </span>
            {entryTypesAndEntryCounts.map(({ type, count }) => {
              const isActive = entryTypeFilter === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setEntryTypeFilter(type)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition ${
                    isActive
                      ? "bg-chips-gold text-white"
                      : "bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500"
                  }`}
                >
                  {type === "all" ? "all" : <EntryIcon type={type} />}
                  <span className="text-xs opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {associatedCompos && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                Compo
              </span>
              {compoTypesAndEntryCounts.map(({ type, name, count }) => {
                const isActive = compoTypeFilter === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setCompoTypeFilter(type)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition ${
                      isActive
                        ? "bg-chips-gold text-white"
                        : "bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-slate-600 dark:text-slate-300 dark:hover:bg-slate-500"
                    }`}
                  >
                    {name}
                    <span className="text-xs opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {filteredEntries.map((entryJSON, i) => {
        const entry = new Entry(entryJSON);
        const faved = (favoriteEntryIDs || []).includes(entry.id);
        const commed = (commentedEntryIDs || []).includes(entry.id);
        const entryTrophies = (trophies || []).filter(
          (t) => t.entry_id === entry.id
        );

        const handleClick = (e: React.MouseEvent) => {
          const target = e.target as HTMLElement;
          if (target.closest("a") || target.closest("button")) return;
          history.push(entry.linkToEntry);
        };

        return (
          <div
            key={entry.id}
            className="mb-1 flex cursor-pointer flex-row items-center overflow-hidden rounded-lg px-2 py-2 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700/50"
            onClick={handleClick}
          >
            {filteredProfilePics && filteredProfilePics[i].length > 0 && (() => {
              const pics = filteredProfilePics[i];
              const offset = 17; // ~1/3 of 50px avatar height
              const containerHeight = 50 + (pics.length - 1) * offset;
              return (
                <div
                  className="group relative flex-[0_0_50px]"
                  style={{ height: `${containerHeight}px` }}
                >
                  {pics.map((url, avatarIdx) => (
                    <img
                      key={avatarIdx}
                      className="absolute h-[50px] w-[50px] rounded border-2 border-white object-cover dark:border-slate-800"
                      style={{
                        top: `${avatarIdx * offset}px`,
                        zIndex: pics.length - avatarIdx,
                      }}
                      src={url}
                    />
                  ))}
                  {entry.type === "song" && !hidePlay && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md dark:bg-slate-800">
                        <PlaySong entry={entry} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="flex-1 self-start pl-[10px]">
              <div className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
                {entry.type === "art" && (
                  <img
                    className="mr-2 mt-2 h-[40px] w-[40px]"
                    src={entry.uploaded_url}
                    width={50}
                    height={50}
                  />
                )}

                <RLink to={entry.linkToEntry}>{entry.title}</RLink>
              </div>

              <div className="text-sm text-slate-600 dark:text-slate-400">
                <ScoreDisplay entry={entry} scoreOverride={filteredScores?.[i]} />
                {entry.hearts > 0 && (
                  <>
                    {" · "}
                    <span title="Favorites">
                      <i
                        className="fa fa-heart text-red-400"
                        aria-hidden="true"
                      />{" "}
                      {entry.hearts}
                    </span>
                  </>
                )}
                {entry.type === "song" && entry.plays > 0 && (
                  <>
                    {" · "}
                    <span title="Plays">
                      <i
                        className="fa fa-play text-slate-400"
                        aria-hidden="true"
                      />{" "}
                      {entry.plays}
                    </span>
                  </>
                )}
                {filteredCompos && filteredCompos[i] && (
                  <>
                    {" · "}
                    <RLink
                      variant="secondary"
                      to={`/compo/${filteredCompos[i].compo_id}`}
                    >
                      {filteredCompos[i].name}
                    </RLink>
                  </>
                )}
                {(() => {
                  const names = filteredUsernames?.[i]?.length
                    ? filteredUsernames[i]
                    : IsExpandedEntryJSON(entry)
                      ? [
                          entry.username_1,
                          entry.username_2,
                          entry.username_3,
                          entry.username_4,
                          entry.username_5,
                        ].filter((x) => !!x)
                      : [];
                  if (names.length === 0) return null;
                  return (
                    <>
                      {" · "}
                      {names.map((x, idx, arr) => (
                        <span key={x}>
                          {idx > 0 && (idx === arr.length - 1 ? ", and " : ", ")}
                          <RLink variant="secondary" to={"/user/" + x}>
                            {x}
                          </RLink>
                        </span>
                      ))}
                    </>
                  );
                })()}
              </div>

              {!hideStats && (
                <div className="pb-[3px] text-xs text-slate-400 dark:text-slate-500">
                  <EntryStats
                    user={user}
                    entry={entry}
                    faved={faved}
                    commed={commed}
                    trophies={entryTrophies}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
